import * as echarts from 'echarts';
const chartColors = [
  '#0047BB',
  '#050139', // Navy
  '#0DCCD3', // Teal
  '#F44821', // Orange
  '#008000', // Green
  '#DECA2B', // Gold
  '#5060F6', // Iris
  '#5B6770', // Gray
  '#0FACE7', // Sky
  '#ACBCC9', // Silver
  '#4482A7',
  '#F6E09D',
  '#222135',
  '#F49D6E',
  '#14D4C3',
  '#AEEEEE',
  '#6B1E65',
  '#24796C',
  '#E0E5E8',
  '#FFEECB',

  '#C1A875',
];
const chartColors2 = [
  '#18FFFF',
  // '#0DCCD3', // bright teal
  '#F44821', // vivid orange
  '#DECA2B', // true gold
  '#0FACE7', // crisp sky-blue
  '#008000', // rich green
  '#E040FB',
];

const trimKey = (key, maxLength) => {
  return key.length > maxLength ? key.slice(0, maxLength - 3) + '...' : key;
};

export function labelPrependAsType(type, ccy) {
  if (type === 'b') {
    return currencySymbol(ccy);
  } else {
    return '';
  }
}

export function labelAppendAsType(type) {
  switch (type) {
    case '%':
      return '%';
    case 'b':
      return 'B';
    case '%100':
      return '%';
    default:
      return '';
  }
}

export function valueAsType(value, type) {
  switch (type) {
    case '.':
      return value.toFixed(2);
    case '%':
      return value.toFixed(2);
    case '100%':
      return (value * 100).toFixed(2);
    case '.000%':
      return value.toFixed(4);
    case '%100':
      return (value * 100).toFixed(2);
    case 'b':
      return (value / 1000000000.0).toFixed(2);
    case '0':
      return value.toFixed(0);
    case '':
      return value.toFixed(2);
    default:
      return value;
  }
}

export function normalize(val, minmax) {
  if (!minmax) {
    return val;
  }

  if (val < minmax[0]) {
    return undefined;
  } else if (val > minmax[1]) {
    return undefined;
  } else {
    return val;
  }
}

function getLegendData(data) {
  return data.map(
    (fund) => `${fund.ticker}${fund.name ? ' - ' + fund.name : ''}`
  );
}

function getFundLabel(fund) {
  return `${fund.ticker}${fund.name ? ' - ' + fund.name : ''}`;
}

function hasEquity(data) {
  const equities = data.filter(function (value) {
    return (
      value.fundCategoryTier1 && value.fundCategoryTier1.includes('Equity')
    );
  });
  return equities.length > 0;
}

//aggregateFundamentals
function getXData(data, name) {
  const hasEq = hasEquity(data);
  let xData = [];
  let dateCnt = 0;

  for (let x = 0; x < data.length; x++) {
    const actData = data[x][name];
    // don't include benchmark if we have equities so oldest fund is the max dates to show
    if (!actData || (hasEq && data[x].fundCategoryTier1 === 'Benchmarks')) {
      continue;
    }
    if (actData.length > dateCnt) {
      dateCnt = actData.length;
      xData = actData.map((item) => {
        const date = new Date(item.date);
        return `${date.getMonth() + 1}/${date.getFullYear()}`;
      });
    }
  }
  return xData;
}

function getSeriesData(data, xData, aggregate, name, type, minmax) {
  const sData = [];
  for (let d = 0; d < data.length; d++) {
    const aggs = data[d][aggregate];
    if (!aggs) {
      continue;
    }
    const ticker = data[d].ticker;
    // map agg date values to xAxis dates
    const values = xData.map((xDate) => {
      const index = aggs.findIndex((agg) => {
        const date = new Date(agg.date);
        return xDate === `${date.getMonth() + 1}/${date.getFullYear()}`;
      });
      if (index > -1) {
        const valIndex = aggs[index].keys.findIndex((x) => x === name);
        if (valIndex !== -1) {
          return normalize(
            valueAsType(aggs[index].values[valIndex], type),
            minmax
          );
        } else {
          return undefined;
        }
      } else {
        return undefined;
      }
    });
    // create and add the series object
    sData.push({
      name: ticker,
      type: 'line',
      smooth: true,
      symbol: 'none',
      lineStyle: {
        width: 2,
      },
      data: values,
    });
  }
  return sData;
}

export function getCharGraph(displayName, name, type, minmax, data) {
  // popoulate legend data
  const legendData = getLegendData(data);

  // populate xaxis data
  const xaxisData = getXData(data, 'aggregateFundamentals');

  // set series values
  const seriesData = getSeriesData(
    data,
    xaxisData,
    'aggregateFundamentals',
    name,
    type,
    minmax
  );

  // create the chart option with data
  return {
    title: {
      text: '',
      left: 5,
      textStyle: {
        fontStyle: 'normal',
        fontWeight: 900,
        fontSize: 14,
        color: '#8C959B',
      },
    },
    tooltip: tooltipAsType(type),
    legend: {
      itemGap: 20,
      data: legendData,
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      axisLabel: {
        fontSize: 12,
        color: '#8C959B',
        rotate: 45,
        align: 'right',
      },
      data: xaxisData,
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        color: '#8C959B',
        formatter: `${labelPrependAsType(type)}{value}${labelAppendAsType(
          type
        )}`,
      },
    },
    series: seriesData,
    color: chartColors,
  };
}

// Helper for consistent label formatting
// function getFundLabel(fund) {
//   return `${fund.ticker}${fund.name ? ' - ' + fund.name : ''}`;
// }

export function getSectorChart(data) {
  // const legendData = getLegendData(data);

  // Extract sector keys from all funds
  const sectors = data
    .map((fund) =>
      fund.sectorExposures?.length
        ? fund.sectorExposures[fund.sectorExposures.length - 1].keys
        : []
    )
    .flat();

  const xaxisData = [...new Set(sectors)].sort();

  // Build series data
  const seriesData = data
    .map((fund) => {
      const exposure = fund.sectorExposures?.[fund.sectorExposures.length - 1];
      if (!exposure || !exposure.keys || !exposure.values) return null;

      const label = getFundLabel(fund);

      const values = xaxisData.map((sector) => {
        const index = exposure.keys.indexOf(sector);
        return index !== -1
          ? parseFloat((exposure.values[index] * 100).toFixed(2))
          : 0;
      });

      if (!values.length || values.every((val) => val === 0)) return null;

      return {
        name: label,
        type: 'bar',
        barGap: 0,
        data: values,
      };
    })
    .filter(Boolean);

  return {
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    legend: {
      type: 'plain',
      top: 0,
      left: 'center',
      orient: 'horizontal',
      width: '90%',
      itemGap: 12,
      textStyle: {
        fontSize: 11,
        color: '#5B6770',
        overflow: 'truncate', // or 'break' if you want long labels to wrap
      },
      formatter: function (name) {
        return name.length > 90 ? name.slice(0, 60) + '…' : name;
      },
      itemWidth: 14, //  wider circle
      itemHeight: 14, //  taller circle
      icon: 'circle', // optional default for all items (but we're overriding per item anyway)
      data: seriesData.map((s) => ({
        name: s.name,
        icon: 'circle',
      })),
    },

    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0; i < params.length; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      top: 100,
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          rotate: 45,
          interval: 0,
          align: 'right',
          formatter: (param) => param.replace('&amp;', '&'),
        },
        axisPointer: {
          type: 'shadow',
        },
        data: xaxisData,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}

export function getSoeSectorChart(data) {
  // popoulate legend data
  const legendData = getLegendData(data);

  // populate xaxisData
  const sectors = data.map((fund) => {
    if (fund.sectorExposures) {
      return fund.sectorExposures[fund.sectorExposures.length - 1].keys;
    } else {
      return null;
    }
  });

  const allSectors = [].concat(...sectors);
  const uniqueSectors = allSectors.filter((v, i, a) => a.indexOf(v) === i);
  const xaxisData = [...uniqueSectors].sort();
  // set series data
  const seriesData = [];
  const seriesData2 = [
    {
      name: '500',
      type: 'bar',
      // "stack": "soe",
      barGap: 0,
      data: [
        8.81, 10.89, 6.49, 4.8, 11.24, 14.38, 7.76, 27.09, 2.79, 0, 2.77, 2.98,
      ],
    },
    {
      name: 'EPS',
      type: 'bar',
      // "stack": "soe",
      barGap: 0,
      data: [
        10.76, 9.62, 7.33, 3.69, 15.22, 15.72, 6.53, 24, 2.91, 0.06, 1.54, 2.6,
      ],
    },
    {
      name: 'EZM',
      type: 'bar',
      // "stack": "soe",
      barGap: 0,
      data: [
        3.18, 17.15, 3.27, 5.79, 19.51, 8.25, 13.68, 12.02, 8.93, 0.29, 4.4,
        3.54,
      ],
    },
  ];

  for (let d = 0; d < data.length; d++) {
    if (!data[d].sectorExposures) {
      continue;
    }
    const sects = data[d].sectorExposures[data[d].sectorExposures.length - 1];
    const ticker = data[d].ticker;
    const values = xaxisData.map((name) => {
      const index = sects.keys.findIndex((x) => x === name);
      if (index !== -1) {
        return parseFloat((sects.values[index] * 100).toFixed(2));
      } else {
        return 0;
      }
    });
    seriesData.push({ name: ticker, type: 'bar', barGap: 0, data: values });
  }

  // create the chart option with data
  return {
    legend: {
      itemGap: 20,
      data: legendData,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          rotate: 45,
          interval: 0,
          align: 'right',
          formatter: function (param) {
            return param.replace('&amp;', '&');
          },
        },
        axisPointer: {
          type: 'shadow',
        },
        data: xaxisData,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData2,
    color: chartColors,
  };
}
export function getEsgChart(data) {
  // popoulate legend data
  const legendData = getLegendData(data);

  // populate xaxisData
  const riskCat = data.map((fund) => {
    if (fund.esgOverallRiskMetrics) {
      return fund.esgOverallRiskMetrics[fund.esgOverallRiskMetrics.length - 1]
        .keys;
    } else {
      return null;
    }
  });

  const allRiskCat = [].concat(...riskCat);
  const uniqueRiskCat = allRiskCat.filter((v, i, a) => a.indexOf(v) === i);
  let xaxisData = [...uniqueRiskCat];
  // set series data
  const seriesData = [];
  for (let d = 0; d < data.length; d++) {
    if (!data[d].esgOverallRiskMetrics) {
      continue;
    }
    const sects =
      data[d].esgOverallRiskMetrics[data[d].esgOverallRiskMetrics.length - 1];
    const ticker = data[d].ticker;
    const values = xaxisData.map((name) => {
      const index = sects.keys.findIndex((x) => x === name);
      if (index !== -1) {
        return parseFloat((sects.values[index] * 100).toFixed(2));
      } else {
        return 0;
      }
    });
    seriesData.push({ name: ticker, type: 'bar', barGap: 0, data: values });
  }

  //Format Axis Data
  xaxisData = xaxisData.map((name) => {
    if (name === 'riskNegligible') {
      return 'Negligible Risk';
    } else if (name === 'riskLow') {
      return 'Low Risk';
    } else if (name === 'riskMedium') {
      return 'Medium Risk';
    } else if (name === 'riskHigh') {
      return 'High Risk';
    } else if (name === 'riskSevere') {
      return 'Severe Risk';
    } else {
      return name;
    }
  });

  // create the chart option with data
  return {
    legend: {
      itemGap: 20,
      data: legendData,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          rotate: 45,
          interval: 0,
          align: 'right',
          formatter: function (param) {
            return param.replace('&amp;', '&');
          },
        },
        axisPointer: {
          type: 'shadow',
        },
        data: xaxisData,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}

export function getSectorGraph(name, data) {
  // popoulate legend data
  const legendData = getLegendData(data);

  // get the max number of dates and populate xaxis data
  const xaxisData = getXData(data, 'sectorExposures');

  // set series values
  const seriesData = getSeriesData(
    data,
    xaxisData,
    'sectorExposures',
    name,
    '100%'
  );

  // create the chart option with data
  return {
    title: {
      text: `${name.replace('&amp;', '&')} Sector (% weight)`,
      left: 5,
      textStyle: {
        fontStyle: 'italic',
        fontWeight: 400,
        fontSize: 14,
        color: '#8c959b',
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          if (!params[i].value) {
            continue;
          }
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    legend: {
      itemGap: 20,
      data: legendData,
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xaxisData,
      axisLabel: {
        fontSize: 12,
        color: '#8C959B',
        rotate: 45,
        align: 'right',
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value}%',
        fontSize: 12,
        color: '#8C959B',
      },
    },
    series: seriesData,
    color: chartColors,
  };
}

export function getUniqueCountries(data) {
  // Extract all country keys from the last country exposure of each fund
  const allCountries = data.reduce((acc, fund) => {
    if (fund.countryExposures && fund.countryExposures.length) {
      const lastExposure =
        fund.countryExposures[fund.countryExposures.length - 1];
      return acc.concat(lastExposure.keys);
    }
    return acc;
  }, []);

  // Create a Set to filter out duplicate country names
  const uniqueCountries = Array.from(new Set(allCountries));

  return uniqueCountries;
}

export function getCountryBarChart(data) {
  const legendData = getLegendData(data);
  // Compute sorted countries based on aggregated percentage values.
  const allCountries = getSortedCountries(data);

  // Build series data: each fund produces horizontal bars for every country.
  const seriesData = data
    .map((fund, i) => {
      const exposure =
        fund.countryExposures?.[fund.countryExposures.length - 1];
      if (!exposure || !exposure.keys || !exposure.values) return null;

      // Use the matching legend label.
      const label = legendData[i];

      // For every country, assign its percentage (or 0 if not found).
      const values = allCountries.map((country) => {
        const idx = exposure.keys.indexOf(country);
        return idx !== -1
          ? parseFloat((exposure.values[idx] * 100).toFixed(2))
          : 0;
      });

      return {
        name: label,
        type: 'bar',
        barGap: '1%', // space between bar
        barCategoryGap: '1%', // between sets ( countries)
        barWidth: '25', // thickness of bar
        // Add the label configuration to display values on the bars.
        label: {
          show: true,
          position: 'insideRight', // Adjust as needed: 'insideLeft', 'inside', etc.
          color: '#ffffff',
          formatter: '{c}%', // {c} represents the current value.
          fontSize: 8,
        },
        data: values,
      };
    })
    .filter(Boolean);

  return {
    graphic: [
      {
        type: 'image',
        id: 'logo',
        right: 5, // Place logo at upper right.
        top: 5, // Adjusted top margin.
        style: {
          image:
            'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
          width: 100,
          height: 40,
        },
      },
    ],
    legend: {
      type: 'plain',
      top: 0,
      left: 'center',
      orient: 'horizontal',
      width: '90%',
      itemGap: 12,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: {
        fontSize: 11,
        color: '#5B6770',
      },
      formatter: (name) => (name.length > 90 ? name.slice(0, 60) + '…' : name),
      data: legendData.map((name) => ({
        name,
        icon: 'circle',
      })),
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let resp = params[0].name;
        for (let i = 0; i < params.length; i++) {
          resp += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return resp;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      top: 100,
      left: '3%',
      right: '3%',
      bottom: '10%', // Adjust bottom margin if necessary.
      containLabel: true,
    },
    // xAxis shows the percentage values.
    xAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: '{value}%',
          fontSize: 12,
          color: '#8C959B',
        },
      },
    ],
    // yAxis contains the country names.
    yAxis: [
      {
        type: 'category',
        data: allCountries,
        inverse: true,
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}

export function getSortedCountries(data) {
  if (!data || data.length === 0) return [];

  // Use the first fund
  const firstFund = data[0];
  // Assuming exposures are stored as an array where the last element is desired
  const exposure =
    firstFund.countryExposures?.[firstFund.countryExposures.length - 1];
  if (!exposure || !exposure.keys || !exposure.values) return [];

  // Map keys to an array of objects including the percentage weight for each country.
  const countriesWithPercentage = exposure.keys.map((country, idx) => ({
    country,
    percentage: parseFloat((exposure.values[idx] * 100).toFixed(2)),
  }));

  // Sort the array based on the percentage weight in descending order.
  countriesWithPercentage.sort((a, b) => b.percentage - a.percentage);

  // Return only the sorted country names.
  return countriesWithPercentage.map((item) => item.country);
}

export function getCountryLineChart(name, data) {
  // popoulate legend data
  const legendData = getLegendData(data);
  // get the max number of dates and populate xaxis data
  const xaxisData = getXData(data, 'countryExposures');
  // set series values in reverse because some go back longer than others
  const seriesData = getSeriesData(
    data,
    xaxisData,
    'countryExposures',
    name,
    '100%'
  );
  // create the chart option with data
  return {
    title: {
      text: `${name} (% weight)`,
      left: 5,
      textStyle: {
        fontStyle: 'italic',
        fontWeight: 400,
        fontSize: 14,
        color: '#8c959b',
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let respo = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          if (!params[i].value) {
            continue;
          }
          respo += `${params[i].marker} ${params[i].seriesName} : ${params[i].value}% <br/>`;
        }
        return respo;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    legend: {
      itemGap: 20,
      data: legendData,
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xaxisData,
      axisLabel: {
        fontSize: 12,
        color: '#8C959B',
        rotate: 45,
        align: 'right',
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value}%',
        fontSize: 12,
        color: '#8C959B',
      },
    },
    series: seriesData,
    color: chartColors,
  };
}

function currencySymbol(ccy) {
  switch (ccy) {
    case 'USD':
    case 'CAD':
      return '$';
    case 'CHF':
      return 'CHF ';
    case 'EUR':
      return '€';
    default:
      return '';
  }
}

function tooltipAsType(type) {
  const append = labelAppendAsType(type);
  return {
    trigger: 'axis',
    formatter: function (params) {
      let res = params[0].name;
      for (let i = 0, l = params.length; i < l; i++) {
        if (!params[i].value) {
          continue;
        }
        res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}${append}`;
      }
      return res;
    },
    // backgroundColor: '#5B6770',
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderColor: '#050139',
    borderRadius: 0,
    textStyle: {
      color: '#5B6770',
      align: 'left',
    },
  };
}

function getFactorSeries(name, rating, data) {
  const vals = data.map((x) => {
    if (x.multifactorTilts) {
      const index = x.multifactorTilts[0].keys.findIndex(
        (key) => key === rating
      );
      if (index !== -1) {
        return Math.round(x.multifactorTilts[0].values[index] * 100);
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  });

  return {
    name: name,
    type: 'bar',
    barGap: 0,
    stack: 'factor',
    data: vals,
    label: {
      normal: {
        show: true,
        position: 'inside',
        formatter: '{c}%',
        fontSize: 16,
        color: '#fff',
      },
    },
  };
}

export function getStyleT(rawData = [], updateHighlight) {
  // ── OFFSETS ──
  const legendOffset = 0; // extra px to push legend/logo down
  const graphicOffset = 60; // px to push bars/dots/labels below legend
  const jitter = 4; // max random jitter in pixels

  // ----- 1) Dimensions & data parsing -----
  const hBarY = 200 + graphicOffset;
  const hBarH = 50;
  const hBarX0 = 90;
  const hBarX1 = 520;
  const vBarH = 240;
  const vBarW = 60;
  const vBarY0 = hBarY + hBarH / 2 + 40;
  const vBarX = (hBarX0 + hBarX1) / 2;

  const seriesData = (Array.isArray(rawData) ? rawData : [])
    .map((fund) => {
      const pts = fund.styleBoxPlotPoints || [];
      if (!pts.length) return null;
      const last = pts[pts.length - 1];
      const { keys = [], values = [] } = last;

      const iX =
        keys.indexOf('wtStyle_XPlot') >= 0
          ? keys.indexOf('wtStyle_XPlot')
          : keys.indexOf('msStyle_XPlot');
      const iY =
        keys.indexOf('wtSize_YPlot') >= 0
          ? keys.indexOf('wtSize_YPlot')
          : keys.indexOf('msSize_YPlot');
      if (iX < 0 || iY < 0) return null;

      const rawX = +values[iX];
      const rawY = +values[iY];
      if (isNaN(rawX) || isNaN(rawY)) return null;

      const name = fund.name || fund.fundName || fund.ticker;
      return { ticker: fund.ticker, name, rawX, rawY };
    })
    .filter(Boolean);

  // ----- 2) Scale functions (domain: -100→400) -----
  const scaleX = (x) => hBarX0 + ((hBarX1 - hBarX0) * (x + 100)) / 500;
  const scaleY = (y) => vBarY0 + vBarH * (1 - (y + 100) / 500);

  // ----- 3) Dot graphics (with jitter & transparency) -----
  const dotGraphics = seriesData.flatMap((d, i) => {
    const color = chartColors2[i % chartColors2.length];
    const baseX = scaleX(d.rawX);
    const baseY = scaleY(d.rawY);
    const x = baseX + (Math.random() * jitter - jitter / 2);
    const y = baseY + (Math.random() * jitter - jitter / 2);

    const onHover = () => updateHighlight?.(d.ticker, true);
    const onOut = () => updateHighlight?.(d.ticker, false);
    const label = `${d.name} (${d.ticker})`;

    return [
      {
        type: 'circle',
        id: `growth-${d.ticker}`,
        name: label,
        shape: { cx: x, cy: hBarY, r: 6 },
        style: { fill: color, stroke: color, lineWidth: 1, opacity: 0.6 },
        z: 5,
        onmouseover: onHover,
        onmouseout: onOut,
      },
      {
        type: 'circle',
        id: `size-${d.ticker}`,
        name: label,
        shape: { cx: vBarX, cy: y, r: 6 },
        style: { fill: color, stroke: color, lineWidth: 1, opacity: 0.6 },
        z: 5,
        onmouseover: onHover,
        onmouseout: onOut,
      },
    ];
  });

  // ----- 4) Gradient stops -----
  const horizontalStops = [
    { offset: 0, color: '#050139' },
    { offset: 0.25, color: '#38356d' },
    { offset: 0.5, color: '#6c70a1' },
    { offset: 0.75, color: '#a2abcd' },
    { offset: 1, color: '#d9d9e9' },
  ];
  const verticalStops = [
    { offset: 0, color: '#050139' },
    { offset: 0.25, color: '#38356d' },
    { offset: 0.5, color: '#6c70a1' },
    { offset: 0.75, color: '#a2abcd' },
    { offset: 1, color: '#d9d9e9' },
  ];

  // ----- 5) Background bars -----
  const horizontalBarGraphic = {
    type: 'rect',
    shape: {
      x: hBarX0,
      y: hBarY - hBarH / 2,
      width: hBarX1 - hBarX0,
      height: hBarH,
    },
    style: {
      fill: new echarts.graphic.LinearGradient(0, 0, 1, 0, horizontalStops),
    },
    z: -1,
  };

  const verticalBarGraphic = {
    type: 'rect',
    shape: {
      x: vBarX - vBarW / 2,
      y: vBarY0,
      width: vBarW,
      height: vBarH,
    },
    style: {
      fill: new echarts.graphic.LinearGradient(0, 0, 0, 1, verticalStops),
    },
    z: -1,
  };

  // ----- 6) Title, logo, axis labels -----
  const axisLabels = [
    {
      type: 'group',
      left: 'center',
      top: 10,
      children: [
        {
          type: 'image',
          style: {
            image:
              'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
            width: 100,
            height: 40,
          },
          x: -100,
          y: -20,
        },
        {
          type: 'text',
          style: {
            text: 'Exposure T',
            fontSize: 18,
            fontWeight: 'bold',
            textAlign: 'left',
            textVerticalAlign: 'middle',
            fill: '#000',
          },
          x: 8,
          y: 0,
        },
      ],
      z: 10,
    },
    {
      type: 'text',
      left: 'center',
      top: 210,
      style: {
        text: 'Factor',
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'center',
        textVerticalAlign: 'middle',
        fill: '#050139',
      },
      z: 6,
    },
    {
      type: 'text',
      x: hBarX0 - 10,
      y: hBarY,
      style: {
        text: 'Value',
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'right',
        fill: '#000',
      },
      z: 10,
    },
    {
      type: 'text',
      x: hBarX1 + 10,
      y: hBarY,
      style: {
        text: 'Growth',
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'left',
        fill: '#000',
      },
      z: 10,
    },
    {
      type: 'text',
      x: vBarX,
      y: vBarY0 - 32,
      style: {
        text: 'Market Cap',
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'center',
        fill: '#000',
      },
      z: 10,
    },
    {
      type: 'text',
      x: vBarX,
      y: vBarY0 - 16,
      style: {
        text: 'Large',
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'center',
        fill: '#000',
      },
      z: 10,
    },
    {
      type: 'text',
      x: vBarX,
      y: vBarY0 + vBarH + 14,
      style: {
        text: 'Small',
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'center',
        fill: '#000',
      },
      z: 10,
    },
  ];

  // ----- 7) Legend dummy series -----
  const legendNames = seriesData.map((d) => {
    const full = `${d.name} (${d.ticker})`;
    return full.length > 60 ? full.slice(0, 45) + '…' : full;
  });
  const dummySeries = seriesData.map((d, i) => ({
    name: legendNames[i],
    type: 'scatter',
    symbol: 'circle',
    symbolSize: 6,
    data: [[-1000, -1000]],
    itemStyle: { color: chartColors2[i % chartColors.length] },
    z: 0,
  }));

  // ----- 8) Return the ECharts option -----
  return {
    color: chartColors,
    backgroundColor: '#f0f0f0',
    legend: {
      show: true,
      orient: 'horizontal',
      icon: 'circle',
      selectedMode: 'multiple',
      left: 'center',
      top: 60 + legendOffset,
      itemGap: 12,
      textStyle: { fontSize: 12 },
      data: legendNames,
    },
    series: dummySeries,
    xAxis: { show: false },
    yAxis: { show: false },
    grid: { top: 110 + graphicOffset, bottom: 40, left: 0, right: 0 },
    tooltip: { show: true, trigger: 'item', formatter: '{b}' },
    graphic: [
      horizontalBarGraphic,
      verticalBarGraphic,
      ...dotGraphics,
      ...axisLabels,
    ],
  };
}

export function getFactorChart(factor, data) {
  //name data
  const tickers = getLegendData(data);
  const seriesData = [
    getFactorSeries('Bad', `${factor}-Bad`, data),
    getFactorSeries('OK', `${factor}-OK`, data),
    getFactorSeries('Good', `${factor}-Good`, data),
  ];

  // create the Factor Chart option with data
  return {
    //     legend: {
    //         itemGap: 20,
    //         data: legendData,
    //       },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params) {
        let res = params[0].name;
        for (let i = params.length - 1; i > -1; i--) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      top: '3%',
      left: '3%',
      right: '3%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: tickers,
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
        },
      },
    ],
    yAxis: [
      {
        max: 100,
        type: 'value',
        axisLabel: {
          formatter: '{value}%',
          fontSize: 12,
          color: '#8C959B',
        },
        splitLine: {
          show: false,
        },
      },
    ],
    series: seriesData,
    color: ['#0047BB', '#050139', '#8C959B'],
  };
}

export function getFactorChart2(factor, data) {
  //name data
  const tickers = getLegendData(data);
  const seriesData = [
    getFactorSeries('Bad', `${factor}-Bad`, data),
    getFactorSeries('OK', `${factor}-OK`, data),
    getFactorSeries('Good', `${factor}-Good`, data),
  ];

  // create the Factor Chart option with data
  return {
    //     legend: {
    //         itemGap: 20,
    //         data: legendData,
    //       },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params) {
        let res = params[0].name;
        for (let i = params.length - 1; i > -1; i--) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      top: '3%',
      left: '3%',
      right: '3%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: tickers,
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
        },
      },
    ],
    yAxis: [
      {
        max: 100,
        type: 'value',
        axisLabel: {
          formatter: '{value}%',
          fontSize: 12,
          color: '#8C959B',
        },
        splitLine: {
          show: false,
        },
      },
    ],
    series: seriesData,
    color: ['#0047BB', '#050139', '#8C959B'],
  };
}

// Helper to create consistent labels
// function getFundLabel(fund) {
//   return `${fund.ticker}${fund.name ? ' - ' + fund.name : ''}`;
// }

export function getMarketCapChart(data) {
  // Collect and flatten all market cap keys
  const allMarketCaps = data
    .map((fund) =>
      fund.marketCapExposures?.length
        ? fund.marketCapExposures[fund.marketCapExposures.length - 1].keys
        : []
    )
    .flat();

  const xaxisData = [...new Set(allMarketCaps)].sort();

  // Display labels
  const labelMap = {
    largeCap: 'Large Cap',
    midCap: 'Mid Cap',
    smallCap: 'Small Cap',
  };
  const xaxisDataFormat = xaxisData.map((key) => labelMap[key] || key);

  // Build series data
  const seriesData = data
    .map((fund) => {
      const exposures =
        fund.marketCapExposures?.[fund.marketCapExposures.length - 1];
      if (!exposures || !exposures.keys || !exposures.values) return null;

      const label = getFundLabel(fund);

      const values = xaxisData.map((key) => {
        const index = exposures.keys.indexOf(key);
        return index !== -1
          ? parseFloat((exposures.values[index] * 100).toFixed(2))
          : 0;
      });

      if (!values.length || values.every((val) => val === 0)) return null;

      return {
        name: label,
        type: 'bar',
        barGap: 0,
        data: values,
      };
    })
    .filter(Boolean);

  return {
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    legend: {
      type: 'plain',
      top: 0,
      left: 'center',
      orient: 'horizontal',
      width: '90%',
      itemGap: 12,
      itemWidth: 14, // make the circle bigger
      itemHeight: 14,
      textStyle: {
        fontSize: 11,
        color: '#5B6770',
      },
      formatter: (name) => (name.length > 90 ? name.slice(0, 60) + '…' : name),
      data: data.map((fund) => ({
        name: getFundLabel(fund),
        icon: 'circle',
      })),
    },

    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0; i < params.length; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      top: 100,
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: xaxisDataFormat,
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          interval: 0,
          formatter: (param) => param.replace('&amp;', '&'),
        },
        axisPointer: {
          type: 'shadow',
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}

// Consistent label formatting for legend + series
// function getFundLabel(fund) {
//   return `${fund.ticker}${fund.name ? ' - ' + fund.name : ''}`;
// }

export function getRegionChart(data) {
  const legendData = getLegendData(data);

  // Get all region keys
  const RegionExp = data
    .map((fund) =>
      fund.regionExposures?.length
        ? fund.regionExposures[fund.regionExposures.length - 1].keys
        : []
    )
    .flat();

  const xaxisData = [...new Set(RegionExp)].sort();

  // Optional: display-friendly names
  const labelMap = {
    US: 'United States',
    DM: 'Developed',
    EM: 'Emerging',
  };
  const xaxisDataFormat = xaxisData.map((key) => labelMap[key] || key);

  const seriesData = data
    .map((fund, i) => {
      const exposures = fund.regionExposures?.[fund.regionExposures.length - 1];
      if (!exposures || !exposures.keys || !exposures.values) return null;

      const label = legendData[i];

      const values = xaxisData.map((key) => {
        const index = exposures.keys.indexOf(key);
        return index !== -1
          ? parseFloat((exposures.values[index] * 100).toFixed(2))
          : 0;
      });

      if (!values.length || values.every((val) => val === 0)) return null;

      return {
        name: label,
        type: 'bar',
        barGap: 0,
        data: values,
      };
    })
    .filter(Boolean);

  return {
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    legend: {
      type: 'plain',
      top: 0,
      left: 'center',
      orient: 'horizontal',
      width: '90%',
      itemGap: 12,
      itemWidth: 14, // larger circle
      itemHeight: 14,
      textStyle: {
        fontSize: 11,
        color: '#5B6770',
      },
      formatter: (name) => (name.length > 90 ? name.slice(0, 60) + '…' : name),
      data: legendData.map((name) => ({
        name,
        icon: 'circle',
      })),
    },

    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0; i < params.length; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      top: 80,
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          interval: 0,
          rotate: 45,
          margin: 10,
          formatter: (param) => param.replace('&amp;', '&'),
        },
        axisPointer: {
          type: 'shadow',
        },
        data: xaxisDataFormat,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}

export function getGeoRevZoneChart(data) {
  const legendData = getLegendData(data);

  // Get all region keys
  const GeoRevExp = data
    .map((fund) =>
      fund.geoRevZoneExposures?.length
        ? fund.geoRevZoneExposures[fund.geoRevZoneExposures.length - 1].keys
        : []
    )
    .flat();

  const xaxisData = [...new Set(GeoRevExp)].sort();

  // Optional: display-friendly names
  // const labelMap = {
  //   US: 'United States',
  //   DM: 'Developed',
  //   EM: 'Emerging',
  // };
  const xaxisDataFormat = xaxisData.map((key) => key);

  const seriesData = data
    .map((fund, i) => {
      const exposures =
        fund.geoRevZoneExposures?.[fund.geoRevZoneExposures.length - 1];
      if (!exposures || !exposures.keys || !exposures.values) return null;

      const label = legendData[i];

      const values = xaxisData.map((key) => {
        const index = exposures.keys.indexOf(key);
        return index !== -1
          ? parseFloat((exposures.values[index] * 100).toFixed(2))
          : 0;
      });

      if (!values.length || values.every((val) => val === 0)) return null;

      return {
        name: label,
        type: 'bar',
        barGap: 0,
        data: values,
      };
    })
    .filter(Boolean);

  return {
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    legend: {
      type: 'plain',
      top: 0,
      left: 'center',
      orient: 'horizontal',
      width: '90%',
      itemGap: 12,
      itemWidth: 14, // larger circle
      itemHeight: 14,
      textStyle: {
        fontSize: 11,
        color: '#5B6770',
      },
      formatter: (name) => (name.length > 90 ? name.slice(0, 60) + '…' : name),
      data: legendData.map((name) => ({
        name,
        icon: 'circle',
      })),
    },

    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0; i < params.length; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      top: 80, //
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          interval: 0,
          rotate: 45,
          margin: 10,
          formatter: (param) => param.replace('&amp;', '&'),
        },
        axisPointer: {
          type: 'shadow',
        },
        data: xaxisDataFormat,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}
export function getMaturityChart(data) {
  // Collect all maturity keys
  const MaturityProfile = data
    .map((fund) =>
      fund.maturityExposures?.length
        ? fund.maturityExposures[fund.maturityExposures.length - 1].keys
        : []
    )
    .flat();

  const sortOrder = [
    'Less than 1 Year',
    '1 to 3 Years',
    '3 to 5 Years',
    '5 to 10 Years',
    '10 to 20 Years',
    '20 to 30 Years',
    'Greater than 30 Years',
  ];

  const xaxisData = [...new Set(MaturityProfile)].sort((a, b) => {
    const aIdx = sortOrder.indexOf(a);
    const bIdx = sortOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  const seriesData = data
    .map((fund) => {
      const exposures =
        fund.maturityExposures?.[fund.maturityExposures.length - 1];
      if (!exposures || !exposures.keys || !exposures.values) return null;

      const label = getFundLabel(fund);

      const values = xaxisData.map((name) => {
        const index = exposures.keys.indexOf(name);
        return index !== -1
          ? parseFloat((exposures.values[index] * 100).toFixed(2))
          : 0;
      });

      // Only include series if values are meaningful
      if (!values.length || values.every((val) => val === 0)) return null;

      return {
        name: label,
        type: 'bar',
        barGap: 0,
        data: values,
      };
    })
    .filter(Boolean);

  return {
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    legend: {
      type: 'plain',
      top: 0,
      left: 'center',
      orient: 'horizontal',
      width: '90%',
      itemGap: 20,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: {
        fontSize: 11,
        color: '#5B6770',
      },
      formatter: (name) => (name.length > 90 ? name.slice(0, 60) + '…' : name),
      data: data.map((fund) => ({
        name: getFundLabel(fund),
        icon: 'circle',
      })),
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: xaxisData,
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          interval: 0,
          formatter: (param) => param.replace('&amp;', '&'),
        },
        axisPointer: {
          type: 'shadow',
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}

export function getCreditQualityChart(data) {
  const CreditProfile = data
    .map((fund) =>
      fund.creditQualityExposures?.length
        ? fund.creditQualityExposures[fund.creditQualityExposures.length - 1]
            .keys
        : []
    )
    .flat();

  const sortOrder = [
    'AAA',
    'AA',
    'A',
    'BBB',
    'BB',
    'B',
    'CCC',
    'CC',
    'C & Below',
    'Cash',
    'Other',
    'N/R',
    'NR',
  ];

  const xaxisData = [...new Set(CreditProfile)].sort((a, b) => {
    const aIdx = sortOrder.indexOf(a);
    const bIdx = sortOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  const seriesData = data
    .map((fund) => {
      const exposures =
        fund.creditQualityExposures?.[fund.creditQualityExposures.length - 1];
      if (!exposures || !exposures.keys || !exposures.values) return null;

      const label = getFundLabel(fund);

      const values = xaxisData.map((name) => {
        const index = exposures.keys.indexOf(name);
        return index !== -1
          ? parseFloat((exposures.values[index] * 100).toFixed(2))
          : 0;
      });

      return {
        name: label,
        type: 'bar',
        barGap: 0,
        data: values,
      };
    })
    .filter(Boolean);

  return {
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    legend: {
      type: 'plain',
      top: 0,
      left: 'center',
      orient: 'horizontal',
      width: '90%',
      itemGap: 20,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: {
        fontSize: 11,
        color: '#5B6770',
      },
      formatter: (name) => (name.length > 90 ? name.slice(0, 60) + '…' : name),
      data: data.map((fund) => ({
        name: getFundLabel(fund),
        icon: 'circle',
      })),
    },

    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0; i < params.length; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: xaxisData,
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          interval: 0,
          formatter: (param) => param.replace('&amp;', '&'),
        },
        axisPointer: {
          type: 'shadow',
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}

export function getAssetClassChart(data) {
  // popoulate legend data
  const legendData = getLegendData(data);

  // populate xaxisData
  const AssetClass = data.map((fund) => {
    if (fund.assetClassExposures) {
      return fund.assetClassExposures[fund.assetClassExposures.length - 1].keys;
    } else {
      return null;
    }
  });
  const allAssetClass = [].concat(...AssetClass);
  const uniqueAssetClass = allAssetClass.filter(
    (v, i, a) => a.indexOf(v) === i
  );
  const xaxisData = [...uniqueAssetClass].sort();

  const seriesData = [];
  for (let d = 0; d < data.length; d++) {
    if (!data[d].assetClassExposures) {
      continue;
    }
    const assets =
      data[d].assetClassExposures[data[d].assetClassExposures.length - 1];
    const ticker = data[d].ticker;
    const values = xaxisData.map((name) => {
      const index = assets.keys.findIndex((x) => x === name);
      if (index !== -1) {
        return parseFloat((assets.values[index] * 100).toFixed(2));
      } else {
        return 0;
      }
    });
    seriesData.push({ name: ticker, type: 'bar', barGap: 0, data: values });
  }

  // create the chart option with data
  return {
    legend: {
      itemGap: 20,
      data: legendData,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = params[0].name;
        for (let i = 0, l = params.length; i < l; i++) {
          res += `<br/>${params[i].marker} ${params[i].seriesName} : ${params[i].value}%`;
        }
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          interval: 0,
          formatter: function (param) {
            return param.replace('&amp;', '&');
          },
        },
        axisPointer: {
          type: 'shadow',
        },
        data: xaxisData,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#8C959B',
          formatter: '{value}%',
        },
      },
    ],
    series: seriesData,
    color: chartColors,
  };
}
//Performance Correlation
export function getCorrelationChart(funds) {
  const originalLabelsX = funds[0].correlations?.keys;
  const originalLabelsY = funds[0].correlations?.keys?.slice().reverse();

  const labelsX = originalLabelsX?.map((label) => trimKey(label, 10));
  const labelsY = originalLabelsY?.map((label) => trimKey(label, 10));

  const data = [];
  for (let x = 0; x < funds.length; x++) {
    let yCnt = 0;
    for (let y = funds[x].correlations.values.length - 1; y >= 0; y--) {
      data.push([x, yCnt, funds[x].correlations.values[y].toFixed(2)]);
      yCnt++;
    }
  }

  return {
    grid: {
      top: '3%',
      left: '0%',
      right: '0%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labelsX,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        margin: 10,
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
      position: 'top',
    },
    yAxis: {
      type: 'category',
      data: labelsY,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
    },
    visualMap: {
      type: 'piecewise',
      splitNumber: 20,
      min: -1,
      max: 1,
      calculable: true,
      show: false,
      inRange: {
        // color: ['#8c959b', '#0047BB','#050139'],
        color: ['#8c959b', '#0047BB'],
      },
    },
    itemStyle: {
      borderWidth: 2,
      borderColor: '#FFF',
    },
    series: [
      {
        name: 'Correlation',
        type: 'heatmap',
        data: data,
        label: {
          show: true,
          fontSize: 20,
          color: '#fff',
        },
      },
    ],
    tooltip: {
      position: 'top',
      formatter: function (params) {
        const labelX = originalLabelsX[params.data[0]].toUpperCase();
        const labelY = originalLabelsY[params.data[1]].toUpperCase();
        return `${labelX} - ${labelY}: ${params.data[2]}`;
      },
    },
  };
}

//Holding Weights in Common
export function getHoldingsOverlapChart(funds) {
  const originalLabelsX = funds[0].overlaps?.keys;
  const originalLabelsY = funds[0].overlaps?.keys?.slice().reverse();

  const labelsX = originalLabelsX?.map((label) => trimKey(label, 10));
  const labelsY = originalLabelsY?.map((label) => trimKey(label, 10));

  const totalHoldings = [];
  const data = [];

  const newLabelsX = [];
  const newLabelsY = [];

  for (let x = 0; x < funds.length; x++) {
    let yCnt = 0;
    for (let y = funds[x].overlaps.values.length - 1; y >= 0; y--) {
      data.push([x, yCnt, Math.round(100 * funds[x].overlaps.values[y])]);
      yCnt++;
    }
    totalHoldings.push(funds[x].topHoldings?.keys?.length);
  }

  let totalHoldingsReversed = totalHoldings?.slice()?.reverse();

  labelsX?.forEach((f, index) => {
    const thold = totalHoldings[index];
    newLabelsX.push(f + ' (' + thold + ') ');
  });

  labelsY?.forEach((f, index) => {
    const thold = totalHoldingsReversed[index];
    newLabelsY.push(f + ' (' + thold + ') ');
  });

  return {
    grid: {
      top: '3%',
      left: '0%',
      right: '0%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labelsX,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        margin: 10,
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
      position: 'top',
    },
    yAxis: {
      type: 'category',
      data: labelsY,
      axisLine: {
        show: true,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
    },
    visualMap: {
      type: 'piecewise',
      splitNumber: 25,
      min: 0,
      max: 100,
      calculable: true,
      show: false,
      inRange: {
        color: ['#8c959b', '#0047BB'],
      },
    },
    itemStyle: {
      borderWidth: 2,
      borderColor: '#FFF',
    },
    series: [
      {
        name: 'Holdings Overlaps',
        type: 'heatmap',
        data: data,
        label: {
          show: true,
          fontSize: 20,
          formatter: function (params) {
            return `${params.value[2]}%`;
          },
          color: '#fff',
        },
      },
    ],
    tooltip: {
      position: 'top',
      formatter: function (params) {
        const labelX = originalLabelsX[params.data[0]].toUpperCase();
        const labelY = originalLabelsY[params.data[1]].toUpperCase();
        return `${labelX} - ${labelY}: ${params.data[2]}`;
      },
    },
  };
}
//Holding Counts in common
export function getHoldingsOverlapCountChart(funds) {
  const originalLabelsX = funds[0].overlaps?.keys;
  const originalLabelsY = funds[0].overlaps?.keys?.slice().reverse();

  const labelsX = originalLabelsX?.map((label) => trimKey(label, 10));
  const labelsY = originalLabelsY?.map((label) => trimKey(label, 10));

  // const labelsXCounts = funds[0].overlapsCount.keys;
  // const labelsYCounts = funds[0].overlapsCount.keys.slice().reverse();
  const totalHoldings = [];
  const data = [];
  let maxChart = 0;
  const newLabelsX = [];
  const newLabelsY = [];

  for (let x = 0; x < funds.length; x++) {
    let maxValue = Math.max(...funds[x].overlapsCount.values);

    let yCnt = 0;
    for (let y = funds[x].overlapsCount.values.length - 1; y >= 0; y--) {
      data.push([x, yCnt, funds[x].overlapsCount.values[y]]);
      yCnt++;
    }
    totalHoldings.push(funds[x].topHoldings?.keys?.length);
    if (maxValue >= maxChart) {
      maxChart = maxValue;
    }
  }

  let totalHoldingsReversed = totalHoldings?.slice()?.reverse();

  labelsX?.forEach((f, index) => {
    const thold = totalHoldings[index];
    newLabelsX.push(f + ' (' + thold + ') ');
  });

  labelsY?.forEach((f, index) => {
    const thold = totalHoldingsReversed[index];
    newLabelsY.push(f + ' (' + thold + ') ');
  });

  return {
    grid: {
      top: '3%',
      left: '0%',
      right: '0%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labelsX,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        margin: 10,
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
      position: 'top',
    },
    yAxis: {
      type: 'category',
      data: labelsY,
      axisLine: {
        show: true,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
    },
    visualMap: {
      type: 'piecewise',
      splitNumber: 25,
      min: 0,
      max: maxChart,
      calculable: true,
      show: false,
      inRange: {
        color: ['#8c959b', '#0047BB'],
      },
    },
    itemStyle: {
      borderWidth: 2,
      borderColor: '#FFF',
    },
    series: [
      {
        name: 'Holdings Overlaps',
        type: 'heatmap',
        data: data,
        label: {
          show: true,
          fontSize: 20,
          color: '#fff',
          // formatter: function (params) {
          //   return `${params.value[2]}%`;
          // },
        },
      },
    ],
    tooltip: {
      position: 'top',
      formatter: function (params) {
        const labelX = originalLabelsX[params.data[0]].toUpperCase();
        const labelY = originalLabelsY[params.data[1]].toUpperCase();
        return `${labelX} - ${labelY}: ${params.data[2]}`;
      },
    },
  };
}
//Relative to first selected fund?
export function getHoldingsOverlapRelativeLeftChart(funds) {
  const originalLabelsX = funds[0].overlaps?.keys;
  const originalLabelsY = funds[0].overlaps?.keys?.slice().reverse();

  if (!originalLabelsX) {
    return null;
  }

  const labelsX = originalLabelsX?.map((label) => trimKey(label, 10));
  const labelsY = originalLabelsY?.map((label) => trimKey(label, 10));

  const data = [];

  for (let x = 0; x < funds.length; x++) {
    let yCnt = 0;
    for (let y = funds[x].overlapsWeightLeft.values.length - 1; y >= 0; y--) {
      data.push([
        x,
        yCnt,
        Math.round(100 * funds[x].overlapsWeightLeft.values[y]),
      ]);
      yCnt++;
    }
  }
  return {
    grid: {
      top: '3%',
      left: '0%',
      right: '0%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labelsX,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        margin: 10,
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
      position: 'top',
    },
    yAxis: {
      type: 'category',
      data: labelsY,
      axisLine: {
        show: true,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
    },
    visualMap: {
      type: 'piecewise',
      splitNumber: 25,
      min: 0,
      max: 100,
      calculable: true,
      show: false,
      inRange: {
        color: ['#8c959b', '#0047BB'],
      },
    },
    itemStyle: {
      borderWidth: 2,
      borderColor: '#FFF',
    },
    series: [
      {
        name: 'Holdings Overlaps',
        type: 'heatmap',
        data: data,
        label: {
          show: true,
          fontSize: 20,
          formatter: function (params) {
            return `${params.value[2]}%`;
          },
          color: '#fff',
        },
      },
    ],
    tooltip: {
      position: 'top',
      formatter: function (params) {
        const labelX = originalLabelsX[params.data[0]];
        const labelY = originalLabelsY[params.data[1]];
        return `${labelX} - ${labelY}: ${params.data[2]}`;
      },
    },
  };
}
//relative to?
export function getHoldingsOverlapRelativeRightChart(funds) {
  const originalLabelsX = funds[0].correlations?.keys;

  if (!originalLabelsX) {
    return null;
  }

  const originalLabelsY = funds[0].correlations?.keys?.slice().reverse();
  const labelsX = originalLabelsX?.map((label) => trimKey(label, 10));
  const labelsY = originalLabelsY?.map((label) => trimKey(label, 10));
  const dataRight = [];

  for (let x = 0; x < funds.length; x++) {
    let yCnt = 0;
    for (let y = funds[x].overlapsWeightRight.values.length - 1; y >= 0; y--) {
      dataRight.push([
        x,
        yCnt,
        Math.round(100 * funds[x].overlapsWeightRight.values[y]),
      ]);
      yCnt++;
    }
  }

  return {
    grid: {
      top: '3%',
      left: '0%',
      right: '0%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labelsX,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        margin: 10,
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
      position: 'top',
    },
    yAxis: {
      type: 'category',
      data: labelsY,
      axisLine: {
        show: true,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontSize: 14,
        color: '#5b6770',
        formatter: function (value) {
          return value.toUpperCase();
        },
      },
    },
    visualMap: {
      type: 'piecewise',
      splitNumber: 25,
      min: 0,
      max: 100,
      calculable: true,
      show: false,
      inRange: {
        color: ['#8c959b', '#0047BB'],
      },
    },
    itemStyle: {
      borderWidth: 2,
      borderColor: '#FFF',
    },
    series: [
      {
        name: 'Holdings Overlaps',
        type: 'heatmap',
        data: dataRight,
        label: {
          show: true,
          fontSize: 20,
          color: '#fff',
          // formatter: function (params) {
          //   return `${params.value[2]}%`;
          // },
        },
      },
    ],
    tooltip: {
      position: 'top',
      formatter: function (params) {
        const labelX = originalLabelsX[params.data[0]];
        const labelY = originalLabelsY[params.data[1]];
        return `${labelX} - ${labelY}: ${params.data[2]}`;
      },
    },
  };
}

// charts.js — replace your existing getGicsTreeMapOption with this
export function getGicsTreeMapOption(
  data,
  selector,
  level = 'sector',
  opts = {}
) {
  // const titleLabel = typeof opts.titleLabel === 'string' ? opts.titleLabel : '';

  // const LEVEL_LABELS = {
  //   sector: 'Sectors',
  //   ig: 'Industry Groups',
  //   industry: 'Industries',
  //   subIndustry: 'Sub-Industries',
  // };
  const SECTOR_ORDER = [
    '10',
    '15',
    '20',
    '25',
    '30',
    '35',
    '40',
    '45',
    '50',
    '55',
    '60',
  ];
  const leafDepth =
    level === 'sector' ? 1 : level === 'ig' ? 2 : level === 'industry' ? 3 : 4;

  const sectors = pickSectors(data, selector) || [];

  // helpers (scoped to avoid duplicate identifiers elsewhere in file)
  const pct = (w) => (Number.isFinite(w) ? w * 100 : 0);
  const sum = (arr) => arr.reduce((s, v) => s + (Number(v) || 0), 0);
  const slug = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // sectorCode → color map (expects chartColors[11+] defined above in this file)
  const sectorColorByCode = new Map();
  SECTOR_ORDER.forEach((code, idx) => {
    sectorColorByCode.set(code, chartColors[idx % chartColors.length]);
  });

  // build full nested tree (all sectors); descendants inherit sector color
  const tree = (sectors || []).map((s, sIdx) => {
    const sectorCode = String(s?.sectorCode ?? '');
    const secColor = sectorColorByCode.get(sectorCode);
    const secId = `S:${sectorCode || slug(s?.sectorName) || sIdx}`;

    const igChildren = (s?.industryGroups || []).map((ig, igIdx) => {
      const igId = `IG:${
        ig?.industryGroupCode || `${sectorCode}:${slug(ig?.gicsName) || igIdx}`
      }`;

      const indChildren = (ig?.industries || []).map((ind, indIdx) => {
        const indId = `IND:${
          ind?.industryCode ||
          `${ig?.industryGroupCode}:${slug(ind?.gicsName) || indIdx}`
        }`;

        const siChildren = (ind?.subIndustries || []).map((si, siIdx) => ({
          id: `SI:${ind?.industryCode || ig?.industryGroupCode}:${
            slug(si?.gicsName) || siIdx
          }`,
          name: si?.gicsName || 'Unknown',
          value: pct(si?.gicsWeight),
          sectorCode,
          industryGroupCode: ig?.industryGroupCode,
          industryCode: ind?.industryCode,
          _path: `${s?.sectorName} › ${ig?.gicsName} › ${ind?.gicsName} › ${si?.gicsName}`,
          itemStyle: secColor ? { color: secColor } : undefined,
        }));

        const indVal = siChildren.length
          ? sum(siChildren.map((c) => c.value))
          : pct(ind?.gicsWeight);
        return {
          id: indId,
          name: ind?.gicsName || 'Unknown',
          value: indVal,
          sectorCode,
          industryGroupCode: ig?.industryGroupCode,
          industryCode: ind?.industryCode,
          _path: `${s?.sectorName} › ${ig?.gicsName} › ${ind?.gicsName}`,
          children: siChildren.length ? siChildren : undefined,
          itemStyle: secColor ? { color: secColor } : undefined,
        };
      });

      const igVal = indChildren.length
        ? sum(indChildren.map((c) => c.value))
        : pct(ig?.gicsWeight);
      return {
        id: igId,
        name: ig?.gicsName || 'Unknown',
        value: igVal,
        sectorCode,
        industryGroupCode: ig?.industryGroupCode,
        _path: `${s?.sectorName} › ${ig?.gicsName}`,
        children: indChildren.length ? indChildren : undefined,
        itemStyle: secColor ? { color: secColor } : undefined,
      };
    });

    const secVal = igChildren.length
      ? sum(igChildren.map((c) => c.value))
      : pct(s?.gicsWeight);
    return {
      id: secId,
      name: s?.sectorName || 'Unknown',
      value: secVal,
      sectorCode,
      _path: s?.sectorName || 'Unknown',
      children: igChildren.length ? igChildren : undefined,
      itemStyle: secColor ? { color: secColor } : undefined,
    };
  });

  // sum of visible nodes at the current leaf depth
  // const totalAtLevel = (() => {
  //   const vals = [];
  //   const walk = (nodes, depth) => {
  //     for (const n of nodes || []) {
  //       if (depth === leafDepth || !n.children) vals.push(Number(n.value) || 0);
  //       else walk(n.children, depth + 1);
  //     }
  //   };
  //   walk(tree, 1);
  //   return vals.reduce((a, b) => a + b, 0);
  // })();

  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (info) => {
        const d = info?.data || {};
        const path = d._path || d.name || '';
        // const code =
        //   d.sectorCode || d.industryGroupCode || d.industryCode || '';
        const v = Number(d.value);
        const valTxt = Number.isFinite(v) ? v.toFixed(2) : '0.00';
        return (
          `<div style="min-width:220px">` +
          `<div><b>${path}</b></div>` +
          `<div><b>Weight:</b> ${valTxt}%</div>` +
          `</div>`
        );
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: { color: '#5B6770', align: 'left' },
    },
    series: [
      {
        name: 'GICS TREEMAP (% WEIGHT)',
        type: 'treemap',
        left: 4,
        right: 4,
        top: 4,
        bottom: 6,
        nodeClick: false,
        roam: false,
        breadcrumb: { show: false },
        leafDepth, // sector=1, ig=2, industry=3, subIndustry=4
        colorMappingBy: 'id', // use our explicit sector colors

        // white labels with shadow; show on display leaves only
        label: {
          show: true,
          color: '#fff',
          //label size based on weight
          rich: {
            L: { fontSize: 12, lineHeight: 14, color: '#fff', fontWeight: 400 },
            M: { fontSize: 11, lineHeight: 13, color: '#fff', fontWeight: 400 },
            S: { fontSize: 10, lineHeight: 12, color: '#fff', fontWeight: 400 },
          },
          textShadowColor: 'rgba(0,0,0,0.45)',
          textShadowBlur: 6,
          textShadowOffsetX: 0,
          textShadowOffsetY: 1,
          formatter: (p) => {
            const d = p.data || {};
            const depthInfoLen = Array.isArray(p.treePathInfo)
              ? p.treePathInfo.length
              : 0;
            const nodeDepth = Math.max(1, depthInfoLen - 1); // echarts auto off by 1 need to fix
            const hasKids = Array.isArray(d.children) && d.children.length > 0;
            const isDisplayLeaf = nodeDepth === leafDepth || !hasKids;
            if (!isDisplayLeaf) return '';
            const v = Number(p.value) || 0;
            const bucket = v >= 5 ? 'L' : v >= 1 ? 'M' : 'S';
            const valTxt = v.toFixed(2);
            return `{${bucket}|${p.name}}\n{${bucket}|\n${valTxt}%}`; // add space for readibility
          },
          overflow: 'break',
          ellipsis: '…',
        },
        labelLayout: { hideOverlap: true },
        levels: [
          { itemStyle: { borderColor: '#fff', borderWidth: 2, gapWidth: 2 } },
          { itemStyle: { borderColor: '#fff', borderWidth: 2, gapWidth: 1 } },
          { itemStyle: { borderColor: '#fff', gapWidth: 1 } },
          { itemStyle: { borderColor: '#fff' } },
        ],
        data: tree,
      },
    ],
  };
}

/* ---------------- helpers + logging ---------------- */

function pickSectors(data, selector) {
  // Case 1: Already the sectors array
  if (Array.isArray(data) && looksLikeSectorsArray(data)) {
    console.log('[GICS] data looks like sectors array');
    return data;
  }

  // Case 2: Object with gicsExposureTree
  if (data && Array.isArray(data.gicsExposureTree)) {
    console.log('[GICS] data has gicsExposureTree');
    return data.gicsExposureTree;
  }

  // Case 3: Funds array (pick one fund, default first)
  if (Array.isArray(data) && looksLikeFundsArray(data)) {
    const fund = selectFund(data, selector);
    // console.log('[GICS] selected fund:', fund);
    const sectors = Array.isArray(fund?.gicsExposureTree)
      ? fund.gicsExposureTree
      : [];
    if (!sectors.length)
      console.warn('[GICS] selected fund has no gicsExposureTree');
    return sectors;
  }

  console.warn('[GICS] unable to detect sectors; returning []');
  return [];
}

function looksLikeSectorsArray(arr) {
  const a = arr?.[0];
  return !!(a && ('sectorName' in a || 'industryGroups' in a));
}

function looksLikeFundsArray(arr) {
  const a = arr?.[0];
  return !!(a && ('gicsExposureTree' in a || 'id' in a || 'name' in a));
}

function selectFund(funds, selector) {
  if (typeof selector === 'number') {
    return funds[selector] ?? funds[0];
  }
  if (typeof selector === 'string') {
    return funds.find((f) => f?.id === selector) ?? funds[0];
  }
  if (typeof selector === 'function') {
    return funds.find((f, i) => selector(f, i, funds)) ?? funds[0];
  }
  return funds[0];
}
